import React, { useEffect, useMemo, useState, useRef } from 'react';
import { PlayIcon, PauseIcon, SettingsIcon, MaximizeIcon, SubtitlesIcon, SkipForwardIcon, SkipBackIcon } from 'lucide-react';
export interface VideoSource {
  src: string;
  vttUrl?: string;
  videoClassName?: string;
}
interface CustomVideoPlayerProps {
  src?: string;
  vttUrl?: string;
  videoClassName?: string;
  playlist?: VideoSource[];
  autoPlay?: boolean;
}
// Parse VTT content into cues
interface VttCue {
  start: number;
  end: number;
  text: string;
}
function parseVttTime(timeStr: string): number {
  const parts = timeStr.trim().split(':');
  if (parts.length === 3) {
    const [h, m, s] = parts;
    return parseFloat(h) * 3600 + parseFloat(m) * 60 + parseFloat(s.replace(',', '.'));
  }
  if (parts.length === 2) {
    const [m, s] = parts;
    return parseFloat(m) * 60 + parseFloat(s.replace(',', '.'));
  }
  return 0;
}
function parseVtt(vttText: string): VttCue[] {
  const cues: VttCue[] = [];
  const blocks = vttText.split(/\n\n+/);
  for (const block of blocks) {
    const lines = block.trim().split('\n');
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].includes('-->')) {
        const [startStr, endStr] = lines[i].split('-->');
        const start = parseVttTime(startStr);
        const end = parseVttTime(endStr);
        const text = lines.slice(i + 1).join('\n').trim();
        if (text) {
          cues.push({
            start,
            end,
            text
          });
        }
        break;
      }
    }
  }
  return cues;
}
export function CustomVideoPlayer({
  src,
  vttUrl,
  videoClassName,
  playlist,
  autoPlay = false
}: CustomVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isPlaylist = playlist && playlist.length > 0;
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentSrc = isPlaylist ? playlist[currentIndex].src : src || '';
  const currentVtt = isPlaylist ? playlist[currentIndex].vttUrl : vttUrl;
  const currentVideoClassName = isPlaylist ? playlist[currentIndex].videoClassName : videoClassName;
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [progress, setProgress] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.25);
  const [showSettings, setShowSettings] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [captionsEnabled, setCaptionsEnabled] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [vttContent, setVttContent] = useState('');
  const [cues, setCues] = useState<VttCue[]>([]);
  // Fetch VTT content when URL changes
  useEffect(() => {
    if (currentVtt) {
      fetch(currentVtt).then(res => res.text()).then(text => {
        setVttContent(text);
        setCues(parseVtt(text));
      }).catch(() => {
        setVttContent('');
        setCues([]);
      });
    } else {
      setVttContent('');
      setCues([]);
    }
  }, [currentVtt]);
  // Get current subtitle text
  const currentSubtitle = useMemo(() => {
    if (!captionsEnabled || cues.length === 0) return '';
    const cue = cues.find(c => currentTime >= c.start && currentTime <= c.end);
    return cue ? cue.text : '';
  }, [currentTime, cues, captionsEnabled]);
  // Apply playback rate on mount and when video changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [currentSrc, playbackRate]);
  // Reset progress when video changes
  useEffect(() => {
    setProgress(0);
    if (isPlaying && videoRef.current) {
      videoRef.current.play().catch(() => setIsPlaying(false));
    }
  }, [currentIndex, currentSrc]);
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const currentProgress = videoRef.current.currentTime / videoRef.current.duration * 100;
      setProgress(currentProgress);
      setCurrentTime(videoRef.current.currentTime);
    }
  };
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (videoRef.current) {
      const time = parseFloat(e.target.value) / 100 * videoRef.current.duration;
      videoRef.current.currentTime = time;
      setProgress(parseFloat(e.target.value));
    }
  };
  const changePlaybackRate = (rate: number) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
      setPlaybackRate(rate);
      setShowSettings(false);
    }
  };
  const toggleFullscreen = () => {
    if (containerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        containerRef.current.requestFullscreen();
      }
    }
  };
  const toggleCaptions = () => {
    setCaptionsEnabled(!captionsEnabled);
  };
  const handleEnded = () => {
    if (isPlaylist && currentIndex < playlist.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setIsPlaying(false);
    }
  };
  const playNext = () => {
    if (isPlaylist && currentIndex < playlist.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };
  const playPrev = () => {
    if (isPlaylist && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };
  return <div ref={containerRef} className="relative w-full rounded-lg overflow-hidden bg-black aspect-video group" onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => {
    setIsHovering(false);
    setShowSettings(false);
  }} data-id="element-61">
      <video ref={videoRef} src={currentSrc} className={`absolute inset-0 w-full h-full cursor-pointer ${currentVideoClassName || 'object-contain'}`} playsInline autoPlay={autoPlay} onTimeUpdate={handleTimeUpdate} onEnded={handleEnded} onClick={togglePlay} onPlay={e => {
      ;
      (e.target as HTMLVideoElement).playbackRate = playbackRate;
    }} onLoadedMetadata={e => {
      ;
      (e.target as HTMLVideoElement).playbackRate = playbackRate;
    }} data-id="element-62" />

      {/* Custom Subtitle Overlay */}
      {currentSubtitle && <div className="absolute bottom-16 sm:bottom-20 left-2 right-2 flex justify-center pointer-events-none z-20" data-id="element-63">
          <div className="bg-black/80 text-white text-xs sm:text-sm md:text-base px-3 py-1.5 rounded text-center max-w-[90%] leading-snug" data-id="element-64">
            {currentSubtitle}
          </div>
        </div>}

      {/* Big Play Button */}
      {!isPlaying && <button onClick={togglePlay} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 sm:w-20 sm:h-20 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-transform hover:scale-105 z-10 shadow-lg" data-id="element-65">
          <PlayIcon className="w-8 h-8 sm:w-10 sm:h-10 text-gray-900 ml-1 sm:ml-2" fill="currentColor" data-id="element-66" />
        </button>}

      {/* Playlist Indicator */}
      {isPlaylist && <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-xs font-medium border border-white/10 z-10" data-id="element-67">
          Part {currentIndex + 1} of {playlist.length}
        </div>}

      {/* Control Bar */}
      <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity duration-300 ${isHovering || !isPlaying ? 'opacity-100' : 'opacity-0'}`} data-id="element-68">
        {/* Progress Bar */}
        <input type="range" min="0" max="100" value={progress || 0} onChange={handleSeek} className="w-full h-1.5 bg-white/30 rounded-lg appearance-none cursor-pointer mb-4 accent-[#7fe0c3]" data-id="element-69" />

        <div className="flex items-center justify-between" data-id="element-70">
          <div className="flex items-center gap-4" data-id="element-71">
            {isPlaylist && <button onClick={playPrev} className={`transition-colors ${currentIndex > 0 ? 'text-white hover:text-[#7fe0c3]' : 'text-white/30 cursor-not-allowed'}`} disabled={currentIndex === 0} data-id="element-72">
                <SkipBackIcon className="w-5 h-5" fill="currentColor" data-id="element-73" />
              </button>}

            <button onClick={togglePlay} className="text-white hover:text-[#7fe0c3] transition-colors" data-id="element-74">
              {isPlaying ? <PauseIcon className="w-6 h-6" fill="currentColor" data-id="element-75" /> : <PlayIcon className="w-6 h-6" fill="currentColor" data-id="element-76" />}
            </button>

            {isPlaylist && <button onClick={playNext} className={`transition-colors ${currentIndex < playlist.length - 1 ? 'text-white hover:text-[#7fe0c3]' : 'text-white/30 cursor-not-allowed'}`} disabled={currentIndex === playlist.length - 1} data-id="element-77">
                <SkipForwardIcon className="w-5 h-5" fill="currentColor" data-id="element-78" />
              </button>}
          </div>

          <div className="flex items-center gap-4 relative h-6" data-id="element-79">
            {currentVtt && <button onClick={toggleCaptions} className={`flex items-center justify-center transition-colors ${captionsEnabled ? 'text-[#7fe0c3]' : 'text-white hover:text-[#7fe0c3]'}`} title="Toggle Captions" data-id="element-80">
                <SubtitlesIcon className="w-6 h-6" data-id="element-81" />
              </button>}

            <div className="relative flex items-center justify-center h-full" data-id="element-82">
              <button onClick={() => setShowSettings(!showSettings)} className="flex items-center justify-center text-white hover:text-[#7fe0c3] transition-colors" title="Settings" data-id="element-83">
                <SettingsIcon className="w-6 h-6" data-id="element-84" />
              </button>

              {/* Settings Menu */}
              {showSettings && <div className="absolute bottom-full right-0 mb-4 w-32 bg-gray-900/95 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden shadow-xl z-20" data-id="element-85">
                  <div className="px-3 py-2 border-b border-white/10 text-xs font-semibold text-white/70" data-id="element-86">
                    Speed
                  </div>
                  {[0.5, 1, 1.25, 1.5, 2].map(rate => <button key={rate} onClick={() => changePlaybackRate(rate)} className={`w-full text-left px-3 py-2 text-sm hover:bg-white/10 transition-colors ${playbackRate === rate ? 'text-[#7fe0c3] font-medium' : 'text-white'}`} data-id="element-87">
                      {rate === 1 ? 'Normal' : `${rate}x`}
                    </button>)}
                </div>}
            </div>

            <button onClick={toggleFullscreen} className="flex items-center justify-center text-white hover:text-[#7fe0c3] transition-colors" title="Fullscreen" data-id="element-88">
              <MaximizeIcon className="w-6 h-6" data-id="element-89" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}